import { KdsButton, KdsModal, KdsText } from "@kazaar-platform/design-system";
import { RiAddCircleFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { CardAddress } from "@/components/CardAddress";
import { CollapseSection } from "@/components/CollapseSection/CollapseSection";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import {
  type AddressReadDto,
  OrderItemUpdateCommandDispatchTypeEnum,
} from "@/config/apiClient";
import { useAddress } from "@/hooks";
import { OrderItemConfigTableVersions } from "../OrderItemConfigTableVersions";
import { type ManualAddressBlockProps } from "./ManualAddressBlock.types";

export const ManualAddressBlock = ({
  isModificationRequest = false,
  onPressAddAddress,
  onPressEditAddress,
  onPressEditVersion,
  selectedAddresses,
  onChangeSelectedAddresses,
}: ManualAddressBlockProps) => {
  const { t } = useTranslate();

  const { useGetAddress } = useAddress();
  const { data: organisationAddressesData } = useGetAddress();

  const methods = useFormContext<OrderItemConfigurationSchema>();

  const [isDispatchSectionOpen, setIsDispatchSectionOpen] =
    useState<boolean>(true);
  const [
    isModalExpandedTableVersionsOpen,
    setIsModalExpandedTableVersionsOpen,
  ] = useState<boolean>(false);

  useEffect(() => {
    methods.setValue(
      "dispatchType",
      selectedAddresses.length > 1
        ? OrderItemUpdateCommandDispatchTypeEnum.ManualDispatch
        : OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch,
      { shouldDirty: true }
    );
  }, [selectedAddresses, methods.setValue]);

  const handleDeleteAddressFromSelectedAddresses = (index: number) => {
    onChangeSelectedAddresses(
      selectedAddresses.filter(
        (_, selectedAddressIndex) => selectedAddressIndex !== index
      )
    );
  };

  const getMatchingAddressFromOrganisation = (address: AddressReadDto) =>
    organisationAddressesData?.data?.find(
      (organisationAddress) =>
        organisationAddress.address === address.address &&
        organisationAddress.postalCode === address.postalCode &&
        organisationAddress.recipientName === address.recipientName
    );

  const handlePressEditAddress = (address: AddressReadDto) => {
    const addressFromOrganisation = getMatchingAddressFromOrganisation(address);

    if (addressFromOrganisation) {
      onPressEditAddress(addressFromOrganisation);
    }
  };

  return (
    <>
      {/* Quantities and Versions information */}
      <div className="mb-sp-md flex items-center gap-sp-xs">
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalQuantity")}
          </KdsText>
          <KdsText size="subtitle-sm">
            {methods.getValues("totalQuantity")}
          </KdsText>
        </div>
        <KdsText color="text-bodyvariant">|</KdsText>
        <div className="flex flex-nowrap items-center gap-sp-xs">
          <KdsText color="text-bodyvariant">
            {t("orderItemConfig.totalVersion")}
          </KdsText>
          <KdsText size="subtitle-sm">
            {methods.getValues("versions").filter((v) => !v.isDeleted).length}
          </KdsText>
        </div>
      </div>

      {/* Version */}
      <OrderItemConfigTableVersions
        isModificationRequest={isModificationRequest}
        onExpand={() => setIsModalExpandedTableVersionsOpen(true)}
        onPressEditVersion={onPressEditVersion}
        className="mb-sp-md"
      />

      {/* Dispatch */}
      <CollapseSection
        size="medium"
        isOpen={isDispatchSectionOpen}
        onOpenChange={() => setIsDispatchSectionOpen(!isDispatchSectionOpen)}
        isBlock
        variant="grey"
        title={t("orderItemConfig.section.addressTitle")}
      >
        <div className="flex flex-col gap-4">
          {selectedAddresses.map((address, index) => (
            <CardAddress
              key={`${address.address}_${index}`}
              addressDetails={address}
              className="bg-white"
              onDelete={() => handleDeleteAddressFromSelectedAddresses(index)}
              onEdit={
                getMatchingAddressFromOrganisation(address)
                  ? () => handlePressEditAddress(address)
                  : undefined
              }
            />
          ))}

          <KdsButton
            iconLeft={RiAddCircleFill}
            variant="outlined"
            color="neutral"
            onPress={onPressAddAddress}
          >
            {t("buttons.createNewAddress")}
          </KdsButton>
        </div>
      </CollapseSection>

      {/** * Modal Expanded Table Versions ***/}
      <KdsModal
        dialogProps={{
          dialogHeaderProps: {
            title: t("orderItemConfig.modal.expandedTableVersions.title"),
          },
          content: <OrderItemConfigTableVersions />,
          dialogFooterProps: {
            buttonProps: [
              {
                children: t("buttons.close"),
                color: "neutral",
                variant: "outlined",
                onPress: () =>
                  setIsModalExpandedTableVersionsOpen(
                    !isModalExpandedTableVersionsOpen
                  ),
              },
            ],
          },
        }}
        isOpen={isModalExpandedTableVersionsOpen}
        onOpenChange={() =>
          setIsModalExpandedTableVersionsOpen(!isModalExpandedTableVersionsOpen)
        }
      />
    </>
  );
};
